import { useMemo, useState, type ReactNode } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import { useCreateSegmentEntry, useSegmentOptions, type SegmentOption } from './segment-picker';

/** The editing state a segment-membership leaf holds. */
export type SegmentsConditionState = {
  segmentIds: readonly (number | string)[];
  negate: boolean;
};

type SegmentsRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's segments leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  /**
   * Coerces a picked option's string id to the type this surface's wire uses.
   * Ids arrive as strings from the picker but producers write numbers, and the
   * saved form has to round-trip unchanged.
   */
  toSegmentId: (value: string) => number | string;
  /**
   * Opens the surface's create-segment flow for this row, or undefined where
   * the shortcut isn't offered — the picker then leaves the entry out.
   */
  onCreateSegment?: (nodeId: string) => void;
  /** The wording for the membership select, which reads differently per surface. */
  membershipWording?: { in: string; notIn: string };
};

const DEFAULT_MEMBERSHIP_WORDING = { in: 'in', notIn: 'not in' };

/** A segment-membership condition row: [type] [in / not in] [segments…]. */
export function SegmentsRow({
  id,
  conditionType,
  typeSelect,
  toSegmentId,
  onCreateSegment,
  membershipWording = DEFAULT_MEMBERSHIP_WORDING,
}: SegmentsRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<SegmentsConditionState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);

  // "Select at least one segment." renders only once the picker has been
  // visited (closing its dropdown) — or from the start when the row mounted
  // with segments already chosen — so a freshly added row doesn't open
  // covered in red.
  const [segmentsTouched, setSegmentsTouched] = useState(() => state.segmentIds.length > 0);
  const segmentsErrorMessage = segmentsTouched ? fieldError(errors, 'segmentIds') : undefined;

  const negateOptions = useMemo(
    () => [
      { value: 'false', label: membershipWording.in },
      { value: 'true', label: membershipWording.notIn },
    ],
    [membershipWording],
  );

  const segments = useSegmentOptions();

  // Resolved against the loaded list; a saved condition referencing a segment
  // that hasn't loaded (or no longer exists) still renders a stable entry
  // rather than dropping out of the selection.
  const selectedOptions: SegmentOption[] = useMemo(() => {
    const loaded = new Map(segments.options.map((option) => [option.value, option]));
    return state.segmentIds.map((segmentId) => {
      const stringId = String(segmentId);
      return loaded.get(stringId) ?? { value: stringId, label: stringId, icon: 'segment-manual' };
    });
  }, [segments.options, state.segmentIds]);

  const beforeOptionsComponent = useCreateSegmentEntry(onCreateSegment, id, 'segments-create-new');

  const handleSegmentsChange = (values: string[] | null) => {
    if (node == null) {
      return;
    }
    // Ids already in the state keep their parsed type so a round-trip stays
    // byte-identical; newly picked ones are coerced to numbers, which is what
    // every producer of this wire writes.
    const previousById = new Map(state.segmentIds.map((segmentId) => [String(segmentId), segmentId]));
    const segmentIds = (values ?? []).map((value) => previousById.get(value) ?? toSegmentId(value));
    updateState(node, { segmentIds });
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-negate-select"
        ariaLabel="Segment membership"
        size="sm"
        options={negateOptions}
        value={state.negate ? 'true' : 'false'}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { negate: value === 'true' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
      <Select
        data-testid="dryad-segments-select"
        ariaLabel="Choose segments"
        size="sm"
        value={selectedOptions}
        options={segments.options}
        error={segmentsErrorMessage}
        onChange={handleSegmentsChange}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) {
            setSegmentsTouched(true);
          }
        }}
        isDisabled={meta.isDisabled}
        isMulti
        isSearchable
        canWrap
        placeholder="Segments"
        searchPlaceholder="Search segments…"
        isLoading={segments.isLoading}
        withVirtualizer
        beforeOptionsComponent={beforeOptionsComponent}
      />
    </InlineStack>
  );
}
